import dayjs from 'dayjs';
import * as muiLocales from '@mui/x-date-pickers/locales';

/**
 * Extracts normalized 2-letter language code (e.g., 'en-US' -> 'en').
 */
export const getDayjsLocale = (lang?: string): string => {
  if (!lang) return 'en';
  const code = lang.toLowerCase().split('-')[0];
  if (code === 'hy') return 'hy-am';
  return code;
};

/**
 * Dynamically loads and sets the Dayjs locale on-demand when language changes.
 * Avoids bundling all locales in the main app chunk.
 */
export const setDayjsLocale = async (lang?: string): Promise<string> => {
  const code = getDayjsLocale(lang);
  if (code === 'en') {
    dayjs.locale('en');
    return 'en';
  }

  try {
    const mod = await import(`dayjs/locale/${code}.js`);
    const localeData = mod.default || mod;
    dayjs.locale(code, localeData);
    if (code === 'hy-am') {
      dayjs.locale('hy', localeData);
    }
    dayjs.locale(code);
    return code;
  } catch (err) {
    // If the locale file does not exist, safely fall back to 'en'
    dayjs.locale('en');
    return 'en';
  }
};

/**
 * Dynamically finds the matching MUI X Date Pickers locale dictionary.
 * Matches prefix (e.g. 'ru' -> 'ruRU', 'tr' -> 'trTR', 'es' -> 'esES', 'de' -> 'deDE').
 * If no match is found, defaults to 'enUS'.
 */
export const getMuiDatePickerLocaleText = (lang?: string) => {
  const code = getDayjsLocale(lang);
  if (code === 'en') {
    return muiLocales.enUS?.components?.MuiLocalizationProvider?.defaultProps?.localeText;
  }

  const localesObj = muiLocales as Record<string, any>;
  const matchKey = Object.keys(localesObj).find(
    (key) =>
      key.toLowerCase().startsWith(code) &&
      localesObj[key]?.components?.MuiLocalizationProvider?.defaultProps?.localeText,
  );

  if (
    matchKey &&
    localesObj[matchKey]?.components?.MuiLocalizationProvider?.defaultProps?.localeText
  ) {
    return localesObj[matchKey].components.MuiLocalizationProvider.defaultProps.localeText;
  }

  return muiLocales.enUS?.components?.MuiLocalizationProvider?.defaultProps?.localeText;
};
